/**
 * ResourceCard — the standard "one record in a grid" card.
 *
 * Studies, Foundry objects, workflows and reports all used to hand-roll
 * their own card: different icon treatments, title sizes, badge styles,
 * hover effects and button stacks. This component fixes the anatomy so
 * every catalog in the product reads the same way:
 *
 *   ┌────────────────────────────────────────────────┐
 *   │ [leading] [icon]  EYEBROW             [badges] ⋮│
 *   │                   Title                         │
 *   │                   Two-line description…         │
 *   │                                                 │
 *   │   LABEL            LABEL                        │
 *   │   value            value                        │
 *   ├─────────────────────────────────────────────────┤
 *   │ footerStart                        footer  →    │
 *   └─────────────────────────────────────────────────┘
 *
 * Rules baked in:
 *   - The card itself is the primary action (`onClick`); nested
 *     controls (menu, footer buttons, checkbox) never bubble to it.
 *   - Colour is carried by the tinted icon tile (`tone`) and by
 *     status chips in `badges` — never by stripes, gradients or
 *     filled primary pills.
 *   - Actions live in the footer strip. Aim for one visible action
 *     plus an overflow `menu`; three full-width buttons is a smell.
 *
 *   <ResourceCard
 *     tone="info"
 *     icon={<Database />}
 *     eyebrow="Master data"
 *     title="Products"
 *     description="SKU master with hierarchy and attributes."
 *     badges={<StatusBadge tone="success">Healthy</StatusBadge>}
 *     menu={<DropdownMenu>…</DropdownMenu>}
 *     meta={[{ label: "Records", value: "12,480" }, { label: "Last sync", value: "Today" }]}
 *     footer={<Button variant="ghost" size="sm">Preview <ChevronRight /></Button>}
 *     onClick={() => open(obj)}
 *   />
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { TONE_TILE, type Tone } from "@/lib/tone";
import { Card, CardFooter, type CardProps } from "@/components/ui/card";


export interface ResourceCardMetaItem {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Optional tooltip on the value cell. */
  title?: string;
}


export interface ResourceCardProps
  extends Omit<CardProps, "title" | "onClick" | "interactive"> {
  /** Glyph rendered inside the tinted icon tile (Lucide/Tabler SVG or an <img>). */
  icon?: React.ReactNode;
  /** Colour of the icon tile. Pick per record *type* (studies, master
   *  data, workflows…) so a mixed grid stays scannable. */
  tone?: Tone;
  /** Small uppercase label above the title — category, object type, source. */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /** Clamped to two lines. */
  description?: React.ReactNode;
  /** Status chips, top-right. Prefer <StatusBadge/>. */
  badges?: React.ReactNode;
  /** Overflow menu trigger, top-right after badges. */
  menu?: React.ReactNode;
  /** Slot before the icon — typically a selection <Checkbox/>. */
  leading?: React.ReactNode;
  /** Label/value pairs rendered as a compact definition grid. */
  meta?: ResourceCardMetaItem[];
  metaColumns?: 2 | 3;
  /** Left side of the footer strip (secondary info or quiet actions). */
  footerStart?: React.ReactNode;
  /** Right side of the footer strip — the visible actions. */
  footer?: React.ReactNode;
  /** Primary action for the whole card. */
  onClick?: () => void;
  /** Extra body content between meta and footer. */
  children?: React.ReactNode;
}


const stop = (e: React.SyntheticEvent) => e.stopPropagation();


export const ResourceCard = React.forwardRef<HTMLDivElement, ResourceCardProps>(
  (
    {
      icon, tone = "primary", eyebrow, title, description, badges, menu, leading,
      meta, metaColumns = 2, footerStart, footer, onClick, children,
      className, ...cardProps
    },
    ref,
  ) => {
    const hasFooter = Boolean(footer || footerStart);
    const hasMeta = Boolean(meta && meta.length > 0);

    return (
      <Card
        ref={ref}
        interactive={Boolean(onClick)}
        onClick={onClick}
        className={cn("group flex flex-col", className)}
        {...cardProps}
      >
        <div className="flex items-start gap-3 p-5 pb-4">
          {leading && (
            <div className="flex shrink-0 items-center pt-0.5" onClick={stop}>
              {leading}
            </div>
          )}
          {icon && (
            <div
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
                "[&_svg]:h-5 [&_svg]:w-5 [&_img]:h-5 [&_img]:w-5",
                TONE_TILE[tone],
              )}
            >
              {icon}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {eyebrow && (
              <div className="mb-0.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {eyebrow}
              </div>
            )}
            <h3
              className={cn(
                "truncate text-base font-semibold leading-snug text-foreground transition-colors",
                onClick && "group-hover:text-primary",
              )}
            >
              {title}
            </h3>
            {description && (
              <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {(badges || menu) && (
            <div
              className="-mr-1.5 -mt-1 flex shrink-0 items-center gap-1.5"
              onClick={stop}
            >
              {badges}
              {menu}
            </div>
          )}
        </div>

        {hasMeta && (
          <dl
            className={cn(
              "grid gap-x-4 gap-y-3 px-5 pb-5",
              metaColumns === 3 ? "grid-cols-3" : "grid-cols-2",
            )}
          >
            {meta!.map((m, i) => (
              <div key={i} className="min-w-0">
                <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {m.label}
                </dt>
                <dd
                  className="mt-0.5 truncate text-sm font-semibold tabular-nums text-foreground"
                  title={m.title}
                >
                  {m.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {children && <div className="px-5 pb-5">{children}</div>}

        {hasFooter && (
          <CardFooter
            bordered
            className="mt-auto justify-between gap-3"
            onClick={stop}
          >
            <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
              {footerStart}
            </div>
            <div className="flex shrink-0 items-center gap-1">{footer}</div>
          </CardFooter>
        )}
      </Card>
    );
  },
);
ResourceCard.displayName = "ResourceCard";
